import { CommonModule, UpperCasePipe } from '@angular/common';
import { Component, computed, effect, inject, input, model, output, signal, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { Menu, MenuModule } from 'primeng/menu';
import { PaginatorModule } from 'primeng/paginator';
import { Popover, PopoverModule } from 'primeng/popover';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { BtnConfig } from 'src/app/models/general';
import { IamActionValues } from 'src/app/models/permission';
import { Column, PaginationData, TableInfo } from 'src/app/models/table';
import { TranslationPipe } from 'src/app/pipes/translation/translation-pipe';
import { TranslationService } from 'src/app/services/configuration/translation-service';
import { AppButton } from '../button/app-button';
import { AppFilter } from '../filter/filter';
import { TableCustomizePopup } from '../table-customize-popup/table-customize-popup';
import { AppRecursiveTable } from './recursive-table/recursive-table';

import { Tooltip } from 'primeng/tooltip';
import { ExportConfig, SearchModel } from 'src/app/models/search';
import { StatusClassPipe } from 'src/app/pipes/status-class/status-class-pipe';
import { PermissionService } from 'src/app/services/user/permission-service';
import { TableExportPopup } from '../table-export-popup/table-export-popup';

@Component({
  selector: 'app-table',
  imports: [
    CommonModule,
    UpperCasePipe,
    TableModule,
    CheckboxModule,
    PaginatorModule,
    ButtonModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    InputNumberModule,
    TagModule,
    AppButton,
    MenuModule,
    PopoverModule,
    TableCustomizePopup,
    AppRecursiveTable,
    ProgressBarModule,
    TranslationPipe,
    IconFieldModule,
    InputIconModule,
    DatePickerModule,
    AppFilter,
    ToggleSwitchModule,
    Tooltip,
    StatusClassPipe,
    TableExportPopup
  ],
  templateUrl: './app-table.html',
  styleUrl: './app-table.scss'
})
export class AppTable {
  permissionService = inject(PermissionService);
  route = inject(ActivatedRoute);
  translationService = inject(TranslationService);

  tableInfo = input<TableInfo>(new TableInfo());
  data = input<any[]>([]);
  tableInfoNested = input<TableInfo>(new TableInfo());

  headerBtns = input<BtnConfig[]>([]);
  search = input<boolean>(true);
  filterBtn = input<boolean>(true);
  // customizeBtn = input<boolean>(true);
  showConcurrentUsers = input<boolean>(false);
  filterFields = input<any[]>([]);
  dateField = input<boolean>(true);
  timeField = input<boolean>(true);
  filterData = input<any[]>([]);
  customTime = input<string>();
  dateTargetField = input<boolean>(true);
  customTemplate = input<TemplateRef<any>>();
  exportConfig = input<ExportConfig>();

  filterApply = output<any[]>();
  filterReset = output<void>();
  filterFieldChange = output<any>();
  onPageChange = output<PaginationData>();
  onRowClick = output<number>();
  onRowClickItem = output<any>();
  onChildRowClick = output<number>();
  onChildRowClickItem = output<any>();
  onExpandClick = output<any>();
  onEditClick = output<any>();
  onSecondEditClick = output<any>();
  onMoreItemClick = output<{ item: any; action: string }>();
  onApproveClick = output<number>();
  headerBtnClick = output<string>();
  onInlineSave = output<any>();
  onInlineEdit = output<void>();
  onInlineCancel = output<void>();
  onColumnClick = output<{ column: Column; item: any }>();
  onParentCheckboxToggle = output<{ item: any; checked: boolean }>();

  checkboxStateMap = model<{ [id: number]: boolean }>({});
  checkboxStateMapProMax = model<{ [id: number]: any }>({});

  @ViewChild('table') table!: Table;
  @ViewChild('filter') filter!: Popover;
  @ViewChild('customize') customize!: TableCustomizePopup;
  @ViewChild('exportPopup') exportPopup!: TableExportPopup;
  @ViewChild('moreMenu') moreMenu!: Popover;
  @ViewChild('pageInput') pageInput!: any;

  clonedData: any[] = [];
  rowsPerPageOptions = [10, 50, 100];
  moreMenuItems: MenuItem[] = [];
  // frozenCols: any[];
  // scrollableCols: any[];
  visibleColumns: any[] = [];
  customizeHiddenColumns: any[] = [];
  columnWidths: number[] = [];

  // allChecked: boolean = false;
  editingRowId: number | null = null;
  moduleCode: string | null = null;
  isEditAllowed: boolean = false; //for inline edit table
  isDeleteAllowed: boolean = false; //for delete functionality
  enableStickyCols: boolean = true; //auto detect if there are any sticky columns
  expandedRows: { [id: number]: boolean } = {}; //track expanded rows
  doneLoading: boolean = false;
  activeMoreMenu: any | null = null;
  searchValueCache: string = '';
  selectedRowId: number = 0;

  ngOnInit() {
    this.moduleCode = this.route.snapshot.data['moduleCode'];
    this.checkPermission();

    // this.frozenCols = this.tableInfo().columns.filter(c => c.sticky);
    // this.scrollableCols = this.tableInfo().columns.filter(c => !c.sticky);

    this.enableStickyCols = this.tableInfo().columns.some((col) => col.sticky === true);
    if (this.tableInfo().paginationData.totalPages === 0) {
      this.tableInfo().paginationData.currentPage = 0;
    }

    // if (this.columns()) {
    //   this.visibleColumns = this.columns().filter((c) => c.hide !== true);
    // }

    if (!this.tableInfo().customizeColumns) {
      this.setDoneLoading(true);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.adjustTableState();
    setTimeout(() => {
      this.measureColumnWidth();
    }, 0);

    // this.pageNumber = this.tableInfo().paginationData.currentPage;

    // if (!this.isEmptyCheckboxStateMap()) {
    //   const map = this.checkboxStateMap();
    //   this.someChecked = this.data().some((item) => map && map[item.id]);
    // } else {
    //   this.someChecked = false;
    // }

    // this.data().forEach(item => {
    //   const isParentRow = this.data().some(d => d.id === item.id && d[this.tableInfo().expandableProp]);
    //   if (isParentRow) {
    //     const isParentRowExpanded = this.table.isRowExpanded(item);
    //     if (isParentRowExpanded) {
    //       this.emitParentCheckboxToggle(item, this.getCheckboxState(item.id));
    //     }
    //   }
    // });

    if (!this.tableInfo().paginationData) {
      this.tableInfo().paginationData = new PaginationData();
    }
    this.tableInfo().paginationData.searchValue = this.searchValueCache;

    if (changes['data']) {
      // if data length is 0 after deletion, go to previous page
      if (changes['data'].currentValue && changes['data'].currentValue.length === 0 && this.tableInfo().paginationData.totalPages > 0) {
        // this.tableInfo().paginationData.currentPage--; //backend returns last page so commented out for now
        this.onCustomPageChange();
      }
      const newJson = JSON.stringify(changes['data'].currentValue);
      const previousJson = JSON.stringify(changes['data'].previousValue);

      if (newJson !== previousJson) {
        this.checkExpandedRows();
      }
    }
  }

  async checkPermission() {
    if (this.moduleCode) this.isEditAllowed = await this.permissionService.hasPermission(this.moduleCode, IamActionValues['EDIT']);
    if (this.moduleCode) this.isDeleteAllowed = await this.permissionService.hasPermission(this.moduleCode, IamActionValues['DELETE']);
  }

  //realign frozen columns after data change (sometimes if data length is too long, frozen columns misaligns)
  adjustTableState() {
    if (!this.table) return;

    this.table.resetScrollTop?.();

    // Wait for DOM to update
    setTimeout(() => {
      const root = (this.table as any).el?.nativeElement || this.table.el;
      if (!root) return;

      const frozenCols = Array.from(root.querySelectorAll('.p-datatable-thead .p-datatable-frozen-column')) as HTMLElement[];

      let leftOffset = 0;
      for (const col of frozenCols) {
        col.style.left = `${leftOffset}px`;
        leftOffset += col.offsetWidth;
      }
    }, 0);
  }

  measureColumnWidth() {
    if (!this.table) return;

    const headers = this.table.el.nativeElement.querySelectorAll('thead th');

    let colIndex = 0;
    this.columnWidths = Array.from(headers as HTMLElement[])
      .filter((_, index: number) => {
        if (index > 1) {
          // const col = this.tableInfo().columns[colIndex];
          const col = this.tableInfo().columns[colIndex - 1];

          return !(col.hide === true || col.customizeHide === true);
        }
        colIndex++;
      })
      .map((th: HTMLElement) => th.offsetWidth);
  }

  getSortedBtns() {
    return [...this.headerBtns()].sort((a, b) => {
      // bigger number = more to the left
      // smaller number = more to the right
      return b.sequence - a.sequence;
    });
  }

  buildHeaderParams(col: Column) {
    if (!col.headerTranslationParams || col.headerTranslationParams.length === 0) {
      return {}; // no params -> translator works normally
    }

    // Convert ["MYR", "year"] to {0: "MYR", 1: "year"}
    return col.headerTranslationParams.reduce((acc, val, index) => {
      acc[index] = val;
      return acc;
    }, {} as any);
  }

  setDoneLoading(event: boolean) {
    this.doneLoading = event;
  }

  getDynamicDisplayType(item: any, columnId: string): Column['displayType'] {
    if (typeof item[columnId] === 'object') {
      return 'chips';
    }

    return 'normal';
  }

  isFrozenColumn(column: Column): boolean {
    if (!column.sticky) return false;
    return this.enableStickyCols && column.sticky;
  }

  //#region Pagination
  onSearchChange() {
    if (this.tableInfo().paginationData.searchValue === '') this.onCustomPageChange();
  }

  clearSearch() {
    this.tableInfo().paginationData.searchValue = '';
    this.onCustomPageChange();
  }

  // Function to handle pagination change
  onCustomPageChange(newCurrentPage?: number) {
    // this.tableInfo().paginationData.currentPage = event.page + 1;
    // this.tableInfo().paginationData.pageSize = event.rows;
    this.searchValueCache = this.tableInfo().paginationData.searchValue;

    this.onPageChange.emit({
      searchValue: this.tableInfo().paginationData.searchValue,
      currentPage: newCurrentPage ? newCurrentPage : this.tableInfo().paginationData.currentPage,
      pageSize: this.tableInfo().paginationData.pageSize,
      totalPages: this.tableInfo().paginationData.totalPages,
      totalRecords: this.tableInfo().paginationData.totalRecords
    });
  }

  get startItem(): number {
    return this.tableInfo().paginationData.totalRecords === 0
      ? 0
      : (this.tableInfo().paginationData.currentPage - 1) * this.tableInfo().paginationData.pageSize + 1;
  }

  get endItem(): number {
    const end = this.tableInfo().paginationData.currentPage * this.tableInfo().paginationData.pageSize;
    return end > this.tableInfo().paginationData.totalRecords ? this.tableInfo().paginationData.totalRecords : end;
  }

  onCustomPageNext() {
    if (this.tableInfo().paginationData.currentPage < this.tableInfo().paginationData.totalPages) {
      const newCurrentPage = this.tableInfo().paginationData.currentPage + 1;
      this.onCustomPageChange(newCurrentPage);
    }
  }

  onCustomPagePrevious() {
    if (this.tableInfo().paginationData.currentPage > 1) {
      const newCurrentPage = this.tableInfo().paginationData.currentPage - 1;
      this.onCustomPageChange(newCurrentPage);
    }
  }

  onPageNumberInputChange(input: any) {
    const newCurrentPage = input.value;
    if (this.tableInfo().paginationData.totalPages > 0) {
      if (newCurrentPage) {
        if (newCurrentPage > this.tableInfo().paginationData.totalPages) {
          this.tableInfo().paginationData.currentPage = this.tableInfo().paginationData.totalPages;
        } else if (newCurrentPage < 1) {
          this.tableInfo().paginationData.currentPage = 1;
        } else {
          this.tableInfo().paginationData.currentPage = newCurrentPage;
        }
      } else {
        this.tableInfo().paginationData.currentPage = this.tableInfo().paginationData.currentPage;
      }
    } else {
      this.tableInfo().paginationData.currentPage = 0;
    }

    input.value = this.tableInfo().paginationData.currentPage;
    this.onCustomPageChange();
  }

  onPageSizeChange(event: any) {
    this.tableInfo().paginationData.pageSize = event.value;
    this.onCustomPageChange();
  }
  //#endregion Pagination

  //#region Checkbox
  checkboxCount = computed(() => {
    if (this.tableInfo().useCheckboxProMax) return Object.keys(this.checkboxStateMapProMax() ?? {}).length;

    return Object.keys(this.checkboxStateMap() ?? {}).length;
  });
  someChecked = computed(() => {
    if (this.tableInfo().useCheckboxProMax) return Object.keys(this.checkboxStateMapProMax()).length > 0;

    return Object.keys(this.checkboxStateMap()).length > 0;
  });
  someCheckedInternal = computed(() => {
    const target = this.tableInfo().useCheckboxProMax ? this.checkboxStateMapProMax() : this.checkboxStateMap();
    const checkboxStateMapIds = Object.keys(target);
    const currentPageIds = this.data().map((d: any) => d[this.tableInfo().checkboxStateMapProp]);
    const someChecked = currentPageIds.some((id: any) =>
      checkboxStateMapIds.includes(String(id))
    );

    return someChecked;
  });

  private updateActiveMap(id: number, value: boolean, row?: any) {
    const target = this.tableInfo().useCheckboxProMax ? this.checkboxStateMapProMax : this.checkboxStateMap;

    target.update((map) => {
      const newMap = { ...map };
      if (value === true) newMap[id] = this.tableInfo().useCheckboxProMax ? this.buildMeta(row) : true;
      else delete newMap[id];
      return newMap;
    });
  }

  private buildMeta(row: any) {
    return (
      this.tableInfo().checkboxProMaxProps?.reduce((acc, prop) => {
        acc[prop] = row?.[prop];
        return acc;
      }, {} as any) ?? {}
    );
  }

  //combined function to handle both single checkbox and "Select All" checkbox
  onCheckboxToggle(item: any, isBulk: boolean = false, checked?: boolean, event?: any) {
    let checkedValue = checked;
    const dataMap = new Map(this.data().map((d) => [d[this.tableInfo().checkboxStateMapProp], d]));

    // this.checkboxStateMap.update((map) => {
    // const newMap = { ...map };

    const updateRow = (row: any, value: boolean) => {
      if (row.checkboxDefaultValue && row.checkboxDefaultValue === true) return;

      this.updateActiveMap(row[this.tableInfo().checkboxStateMapProp], value, row);

      // if (value === true) newMap[row[this.tableInfo().checkboxStateMapProp]] = true;
      // else delete newMap[row[this.tableInfo().checkboxStateMapProp]];

      const dataItem = dataMap.get(row[this.tableInfo().checkboxStateMapProp]);
      if (dataItem) dataItem.checked = value;

      if (this.tableInfo().type === 'nested') {
        const isParent = dataItem && dataItem[this.tableInfo().expandableProp];
        if (isParent) {
          const isExpanded = this.table.isRowExpanded(dataItem);

          if (!isExpanded && checkedValue) {
            this.table.toggleRow(dataItem);
            this.emitParentCheckboxToggle(dataItem, checkedValue);
          }
          // else if (checkedValue === false && dataItem.children?.length) {
          //   dataItem.children.forEach((child: any) => updateRow(child, false));
          // }
          else if (isExpanded && dataItem.children?.length) {
            dataItem.children.forEach((child: any) => updateRow(child, checkedValue!));
          }
        }
      }
    };

    // --- CASE 1: Bulk (Select All) ---
    if (isBulk) {
      checkedValue = event.checked;
      this.data().forEach((d) => updateRow(d, checkedValue!));
      // return newMap;
      return;
    }

    // --- CASE 2: Single Checkbox ---
    // const item = item;
    updateRow(item, checkedValue!);
    return;

    // return newMap;
    // });
  }

  getCheckboxState(id: number): boolean {
    if (this.tableInfo().useCheckboxProMax) {
      return (this.someChecked() && id in this.checkboxStateMapProMax()) ?? false;
    }

    return (this.someChecked() && this.checkboxStateMap()[id]) ?? false;
  }

  // Utility function to check if all checkboxes are selected
  // areAllCheckboxesSelected() {
  //   return this.data().every(item => item.checked);
  // }

  // Function to handle "Select All" checkbox change
  onCheckboxAllChange(event: any) {
    this.onCheckboxToggle(null, true, event.checked, event);
  }
  //#endregion Checkbox

  //#region output onclick events
  onRowClickEvent(item: any) {
    const itemId = item.id ?? 0;
    if (this.tableInfo().enableRowClick) {
      this.onRowClick.emit(itemId);
      this.onRowClickItem.emit(item);
    }
    this.selectedRowId = itemId;
  }

  onChildRowClickEvent(id: number) {
    this.onChildRowClick.emit(id);
  }

  onChildRowClickItemEvent(item: any) {
    this.onChildRowClickItem.emit(item);
  }

  onExpand(event: any, item?: any, expanded?: boolean) {
    event.stopPropagation();

    if (!expanded && !this.expandedRows[item.id]) {
      this.expandedRows[item.id] = true;
      this.emitExpand(item);
    } else {
      delete this.expandedRows[item.id];
    }
  }

  emitExpand(item: any) {
    this.onExpandClick.emit(item);
  }

  checkExpandedRows() {
    let rowsToExpand: number[] = [];

    if (this.data() && this.data().length > 0) {
      this.data().forEach((item) => {
        if (this.expandedRows[item.id]) {
          this.emitExpand(item);
        }
      });
    }
  }

  emitParentCheckboxToggle(item: any, checked: boolean) {
    this.onParentCheckboxToggle.emit({ item, checked });
    // this.emitExpand(item);
  }

  onIconClickEvent(item: any, primary: boolean = true) {
    if (primary) {
      this.onEditClick.emit(item);
    } else {
      this.onSecondEditClick.emit(item);
    }
  }

  onHeaderBtnClickEvent(key: string) {
    if (key === 'cancel') {
      this.checkboxStateMap.set({});
      this.checkboxStateMapProMax.set({});
    }
    key !== 'export' ? this.headerBtnClick.emit(key) : this.exportPopup.show();
  }

  async onMoreClickEvent(event: MouseEvent, item: any, menu: Menu) {
    event.stopPropagation();

    let moreOptions: any;

    if (this.tableInfo().moreOptionsType === 'item-based') {
      moreOptions = item.moreOptions;
    } else {
      moreOptions = this.tableInfo().moreOptions;
    }

    // close previously opened menu
    if (this.activeMoreMenu && this.activeMoreMenu !== menu) {
      this.activeMoreMenu.hide();
    }

    moreOptions = (
      await Promise.all(
        moreOptions.map(async (option: any) => {
          if (option.separator) {
            return option;
          }

          option.value = option.value ?? option.label;

          const label = option.label ?? '';
          const required = this.permissionService.getTypeOfPermissionBasedOnLabel(label);

          if (!required) {
            return option;
          }

          const allowed = this.moduleCode
            ? await this.permissionService.hasPermission(
              this.moduleCode,
              required
            )
            : false;

          return allowed ? option : null;
        })
      )
    ).filter((option: any) => option !== null)
      .map((option: any) => {
        if (option.separator) {
          return { separator: true };
        }

        const label = option.label ?? '';

        return {
          ...option,
          disabled:
            item.disableMenuOption === true || option.disabled === true,
          label: this.translationService.translate(label),
          command: () => {
            this.onMoreItemClick.emit({
              item: item,
              action: option.value
            });

            menu.hide();
            this.activeMoreMenu = null;
          }
        };
      });

    const moreOptionsEmpty = moreOptions.length === 0 || (moreOptions.every((m: any) => m.separator === true));
    if (moreOptionsEmpty) {
      item.moreOptions = [{
        disabled: true,
        label: this.translationService.translate('NO_PERMISSION'),
      }];
    }
    else {
      const shouldRemoveSeparator = moreOptions.length === 2 && moreOptions.some((m: any) => m.separator === true)
      item.moreOptions = shouldRemoveSeparator ? moreOptions.filter((m: any) => m.separator !== true) : moreOptions;

    }

    // toggle current menu
    menu.toggle(event);
    this.activeMoreMenu = menu;
  }

  onChildMoreClickEvent(event: any) {
    this.onMoreItemClick.emit(event);
  }

  onColumnclickEvent(column: Column, item: any) {
    this.onColumnClick.emit({ column, item });
  }

  onCustomize() {
    setTimeout(() => {
      this.measureColumnWidth();
    }, 0);
  }
  //#endregion output onclick events

  toggleCustomize(event: any) {
    this.customize.toggle(event);
  }

  toggleFilter(event: any) {
    this.filter.toggle(event);
  }

  handleFilterApply(filters: any[]) {
    this.filterApply.emit(filters);
  }

  emitFilterFieldChange(event: any) {
    this.filterFieldChange.emit(event);
  }

  handleFilterReset() {
    this.filterReset.emit();
  }

  // formatPercentage(value: any): string {
  //   if (value === null || value === undefined || value === '') return '0.00';
  //   let num = parseFloat(value);
  //   if (isNaN(num)) num = 0;
  //   return num.toFixed(2);
  // }

  // percentageFormatTimer: any;

  // formatPercentageDebounced(item: any, key: string) {
  //   clearTimeout(this.percentageFormatTimer);

  //   this.percentageFormatTimer = setTimeout(() => {
  //     const value = item[key];
  //     item[key] = this.formatPercentage(value);
  //   }, 1000);
  // }

  //#region Export
  onExportConfirm(exportColumns: string[]) {
    const config = this.exportConfig();
    if (!config) return;
    const checkboxMap = this.tableInfo().useCheckboxProMax ? this.checkboxStateMapProMax : this.checkboxStateMap;
    let customSearchModel: SearchModel;
    if (Object.keys(checkboxMap()).length === 0) {
      customSearchModel = { ...config.searchModel };
    } else {
      customSearchModel = new SearchModel();
      customSearchModel.criteria = [
        {
          groupLogicalOperator: 1,
          filters: []
        }
      ];
      for (const item of Object.keys(checkboxMap())) {
        customSearchModel.criteria[0].filters.push({
          key: 'id',
          value: item,
          matchType: 0,
          dataType: 0
        });
      }
    }
    delete customSearchModel.pageNumber;
    delete customSearchModel.pageSize;
    let finalExportColumns = exportColumns.flatMap((col) => config.overrideParam?.[col] ?? [col]);
    finalExportColumns = [...finalExportColumns, ...(config.extraParam ?? [])];
    if (config.excludeParam?.length) {
      finalExportColumns = finalExportColumns.filter((col) => !config.excludeParam!.includes(col));
    }

    customSearchModel.exportColumns = finalExportColumns;
    config.api(customSearchModel).subscribe({
      next: (response: any) => {
        const objectUrl = URL.createObjectURL(response.blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = response.fileName;
        a.click();
        URL.revokeObjectURL(objectUrl);
        checkboxMap.set({});
      }
    });
  }
}
